import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  Clock3,
  Eye,
  EyeOff,
  MapPin,
  Store,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import { cn } from '../../lib/utils';
import {
  AuthRequestError,
  type AuthValidationError,
} from '../../lib/api/auth-client';
import { AuthShell } from './auth-shell';
import {
  FORGOT_PASSWORD_PATH,
  REGISTER_PATH,
  resolveAuthenticatedPath,
} from './auth-routing';
import { type PreparedLoginAttempt, useAuth } from './auth-context';

const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'O email é obrigatório.')
    .email('Introduza um email válido.'),
  password: z.string().min(1, 'A palavra-passe é obrigatória.'),
  rememberMe: z.boolean(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LoginLocationState {
  from?: string;
  email?: string;
  notice?: string;
}

const loginHighlights = [
  {
    icon: CalendarDays,
    title: 'Agenda com leitura rápida',
    description: 'Entre e volte logo ao ponto onde deixou a disponibilidade e os pedidos.',
  },
  {
    icon: Clock3,
    title: 'Fluxo mais calmo',
    description: 'Tudo foi pensado para quem gere marcações sem perder tempo a navegar.',
  },
  {
    icon: Store,
    title: 'Foco no negócio',
    description: 'Serviços, horários e perfil do espaço ficam no mesmo percurso visual.',
  },
] as const;

const loginMetrics = [
  { value: '3 passos', label: 'Da conta ao negócio' },
  { value: '1 painel', label: 'Tudo num só lugar' },
  { value: '100%', label: 'Pensado para mobile' },
] as const;

const fieldClassName =
  'h-14 rounded-[1.35rem] border-[#dfd4c6] bg-[#fffdf9] text-[#16323b] shadow-[0_14px_28px_rgba(50,40,27,0.08)] placeholder:text-[#8c8578] focus-visible:ring-[#0f766e]/25 focus-visible:ring-offset-[#fffaf4]';

const labelClassName =
  'block text-left text-sm font-semibold uppercase tracking-[0.18em] text-[#6d665c]';
const passwordFieldClassName = `${fieldClassName} pr-16`;
const passwordToggleButtonClassName =
  'absolute right-3 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-[#9a8c79] transition hover:text-[#0f766e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0f766e]/25';

function getFieldErrorId(fieldName: keyof LoginFormValues) {
  return `${fieldName}-error`;
}

function getLoginValidationMessage(validationError: AuthValidationError) {
  switch (validationError.field) {
    case 'email':
      return validationError.errorType === 'blank'
        ? 'Indique o email.'
        : 'Introduza um email válido.';
    case 'password':
      if (validationError.errorType === 'blank') {
        return 'Indique a palavra-passe.';
      }

      if (validationError.errorType === 'incorrect') {
        return 'O email ou a palavra-passe não estão corretos.';
      }

      return 'Introduza uma palavra-passe válida.';
    default:
      return null;
  }
}

function getBusinessRoleLabel(role: PreparedLoginAttempt['businesses'][number]['role']) {
  if (role === 'staff') {
    return 'Equipa';
  }

  if (role === 'owner') {
    return 'Proprietário';
  }

  return 'Negócio';
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, prepareLogin, completeLogin, session } = useAuth();
  const [showPassword, setShowPassword] = React.useState(false);
  const [pendingLogin, setPendingLogin] = React.useState<PreparedLoginAttempt | null>(
    null
  );
  const [selectedBusinessId, setSelectedBusinessId] = React.useState<number | null>(
    null
  );
  const [businessSelectionError, setBusinessSelectionError] = React.useState<string | null>(
    null
  );
  const [isCompletingLogin, setIsCompletingLogin] = React.useState(false);
  const locationState =
    location.state && typeof location.state === 'object'
      ? (location.state as LoginLocationState)
      : undefined;
  const fromRoute = locationState?.from;
  const emailValueFromState = locationState?.email ?? '';
  const notice = locationState?.notice ?? null;
  const isSelectingBusiness = pendingLogin !== null;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      email: emailValueFromState,
      password: '',
      rememberMe: false,
    },
  });

  const rememberMe = watch('rememberMe');
  const currentEmail = watch('email');

  React.useEffect(() => {
    register('rememberMe');
  }, [register]);

  React.useEffect(() => {
    if (isAuthenticated && session) {
      navigate(resolveAuthenticatedPath(session, fromRoute), { replace: true });
    }
  }, [fromRoute, isAuthenticated, navigate, session]);

  function handleRequestError(error: unknown) {
    if (error instanceof AuthRequestError && error.validationError) {
      const fieldMessage = getLoginValidationMessage(error.validationError);

      if (fieldMessage && error.validationError.field === 'email') {
        setError('email', {
          type: 'server',
          message: fieldMessage,
        });
        return;
      }

      if (fieldMessage && error.validationError.field === 'password') {
        setError('password', {
          type: 'server',
          message: fieldMessage,
        });
        return;
      }

      if (fieldMessage) {
        setError('root.server', {
          type: 'server',
          message: fieldMessage,
        });
        return;
      }
    }

    setError('root.server', {
      type: 'server',
      message:
        error instanceof Error
          ? error.message
          : 'Não foi possível entrar neste momento.',
    });
  }

  function resetPendingLogin() {
    setPendingLogin(null);
    setSelectedBusinessId(null);
    setBusinessSelectionError(null);
  }

  const onSubmit = handleSubmit(async (values) => {
    clearErrors('root');
    setBusinessSelectionError(null);

    try {
      const attempt = await prepareLogin({
        credentials: {
          email: values.email,
          password: values.password,
        },
        email: values.email,
        rememberMe: values.rememberMe,
      });

      if (attempt.businesses.length > 1) {
        setPendingLogin(attempt);
        setSelectedBusinessId(attempt.selectedBusinessId);
        return;
      }

      const nextSession = await completeLogin(
        attempt,
        attempt.selectedBusinessId
      );

      navigate(resolveAuthenticatedPath(nextSession, fromRoute), { replace: true });
    } catch (error) {
      handleRequestError(error);
    }
  });

  async function handleBusinessSelectionSubmit(
    event: React.FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();

    if (!pendingLogin) {
      return;
    }

    if (selectedBusinessId === null) {
      setBusinessSelectionError('Escolha o negócio que quer abrir nesta sessão.');
      return;
    }

    setBusinessSelectionError(null);
    setIsCompletingLogin(true);

    try {
      const nextSession = await completeLogin(pendingLogin, selectedBusinessId);
      navigate(resolveAuthenticatedPath(nextSession, fromRoute), { replace: true });
    } catch (error) {
      setBusinessSelectionError(
        error instanceof Error
          ? error.message
          : 'Não foi possível concluir a entrada neste negócio.'
      );
    } finally {
      setIsCompletingLogin(false);
    }
  }

  const loginForm = (
    <form className="space-y-6" onSubmit={onSubmit} noValidate>
      {notice ? (
        <div
          className="rounded-[1.5rem] border border-[#cae7dc] bg-[#ebf7f1] px-4 py-4 text-sm font-medium text-[#0f766e]"
          role="status"
        >
          {notice}
        </div>
      ) : null}

      <div className="space-y-3">
        <label className={labelClassName} htmlFor="email">
          Email
        </label>
        <Input
          id="email"
          type="email"
          placeholder="seu@email.com"
          autoComplete="email"
          className={cn(
            fieldClassName,
            errors.email && 'border-destructive/60 bg-destructive/5'
          )}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? getFieldErrorId('email') : undefined}
          {...register('email', {
            onChange: () => {
              clearErrors('root.server');
            },
          })}
        />
        {errors.email ? (
          <p
            id={getFieldErrorId('email')}
            className="px-2 text-left text-sm font-medium text-destructive"
          >
            {errors.email.message}
          </p>
        ) : null}
      </div>

      <div className="space-y-3">
        <label className={labelClassName} htmlFor="password">
          Palavra-passe
        </label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            autoComplete="current-password"
            className={cn(
              passwordFieldClassName,
              errors.password && 'border-destructive/60 bg-destructive/5'
            )}
            aria-invalid={Boolean(errors.password)}
            aria-describedby={
              errors.password ? getFieldErrorId('password') : undefined
            }
            {...register('password', {
              onChange: () => {
                clearErrors('root.server');
              },
            })}
          />
          <button
            type="button"
            aria-label={showPassword ? 'Esconder password' : 'Mostrar password'}
            className={passwordToggleButtonClassName}
            onClick={() => setShowPassword((current) => !current)}
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>
        </div>
        {errors.password ? (
          <p
            id={getFieldErrorId('password')}
            className="px-2 text-left text-sm font-medium text-destructive"
          >
            {errors.password.message}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-4 rounded-[1.5rem] border border-[#efe4d7] bg-[#fff7ef]/80 p-4 text-sm font-medium text-[#5f655f] sm:flex-row sm:items-center sm:justify-between">
        <label className="inline-flex cursor-pointer items-center gap-3">
          <Checkbox
            checked={rememberMe}
            onCheckedChange={(checked) =>
              setValue('rememberMe', checked === true, {
                shouldDirty: true,
              })
            }
            aria-label="Manter sessão iniciada"
          />
          <span>Manter sessão iniciada</span>
        </label>

        <Link
          to={FORGOT_PASSWORD_PATH}
          state={{
            email: currentEmail,
          }}
          className="text-left font-semibold text-[#0f766e] transition hover:text-[#0c615b]"
        >
          Esqueceu-se da palavra-passe?
        </Link>
      </div>

      {errors.root?.server?.message ? (
        <div
          className="rounded-[1.5rem] border border-destructive/20 bg-destructive/10 px-4 py-4 text-sm font-medium text-destructive"
          role="alert"
        >
          {errors.root.server.message}
        </div>
      ) : null}

      <Button
        type="submit"
        size="lg"
        className="w-full rounded-[1.35rem] bg-[#0f766e] text-base shadow-[0_20px_40px_rgba(15,118,110,0.24)] hover:bg-[#0c615b]"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'A entrar...' : 'Entrar'}
      </Button>

      <div className="flex items-center gap-4">
        <Separator className="flex-1 bg-[#e6dbcf]" />
        <span className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8b8478]">
          ou
        </span>
        <Separator className="flex-1 bg-[#e6dbcf]" />
      </div>

      <Button
        type="button"
        variant="ghost"
        className="w-full rounded-[1.35rem] border border-[#e5d8ca] bg-[#fff7ef] text-[1.02rem] text-[#16323b] hover:bg-[#f8efe3]"
        onClick={() =>
          navigate(REGISTER_PATH, {
            state: {
              email: currentEmail,
            },
          })
        }
      >
        Como obter acesso
      </Button>
    </form>
  );

  const businessSelectionForm = pendingLogin ? (
    <form className="space-y-6" onSubmit={handleBusinessSelectionSubmit}>
      <div
        className="rounded-[1.5rem] border border-[#cae7dc] bg-[#ebf7f1] px-4 py-4 text-sm font-medium leading-6 text-[#0f766e]"
        role="status"
      >
        Encontrámos vários negócios ligados a <strong>{pendingLogin.email}</strong>.
        Escolha o espaço que quer abrir nesta sessão.
      </div>

      <div
        aria-label="Negócios disponíveis"
        className="space-y-3"
        role="radiogroup"
      >
        {pendingLogin.businesses.map((business) => {
          const isSelected = selectedBusinessId === business.id;

          return (
            <button
              key={business.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={cn(
                'w-full rounded-[1.6rem] border px-4 py-4 text-left transition sm:px-5',
                isSelected
                  ? 'border-[#0f766e] bg-[#f2fbf8] shadow-[0_18px_42px_rgba(15,118,110,0.14)]'
                  : 'border-[#e5d8ca] bg-[#fffdf9] hover:border-[#cdbba5] hover:bg-[#fff8f1]'
              )}
              onClick={() => {
                setSelectedBusinessId(business.id);
                setBusinessSelectionError(null);
              }}
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[1.15rem] bg-[#fff2e4] text-[#9a5d2e]">
                  <Building2 className="h-5 w-5" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-semibold text-[#16323b]">
                      {business.name}
                    </span>
                    <span className="rounded-full border border-[#e8ddcf] bg-white/90 px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#7b6f62]">
                      {getBusinessRoleLabel(business.role)}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-3 text-sm text-[#5f655f]">
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-[#0f766e]" />
                      {business.location ?? 'Localização por definir'}
                    </span>
                    {business.timezone ? (
                      <span className="inline-flex items-center gap-2">
                        <Clock3 className="h-4 w-4 text-[#0f766e]" />
                        {business.timezone}
                      </span>
                    ) : null}
                  </div>
                </div>

                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition',
                    isSelected
                      ? 'border-[#0f766e] bg-[#0f766e]'
                      : 'border-[#d9cbbb] bg-white'
                  )}
                >
                  <span
                    className={cn(
                      'h-2.5 w-2.5 rounded-full transition',
                      isSelected ? 'bg-white' : 'bg-transparent'
                    )}
                  />
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {businessSelectionError ? (
        <div
          className="rounded-[1.5rem] border border-destructive/20 bg-destructive/10 px-4 py-4 text-sm font-medium text-destructive"
          role="alert"
        >
          {businessSelectionError}
        </div>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="button"
          variant="outline"
          className="rounded-[1.25rem] border-[#dfd4c6] bg-white text-[#16323b] hover:bg-[#f8efe3]"
          onClick={resetPendingLogin}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar ao login
        </Button>

        <Button
          type="submit"
          size="lg"
          className="rounded-[1.35rem] bg-[#0f766e] text-base shadow-[0_20px_40px_rgba(15,118,110,0.24)] hover:bg-[#0c615b]"
          disabled={isCompletingLogin}
        >
          {isCompletingLogin ? 'A abrir negócio...' : 'Continuar'}
        </Button>
      </div>
    </form>
  ) : null;

  return (
    <AuthShell
      heroBadge="Acesso de gestor"
      heroTitle="Uma receção digital mais calma para o seu negócio."
      heroDescription="Retome horários, serviços e disponibilidade num ambiente mais elegante e fácil de percorrer, sem sacrificar rapidez."
      heroHighlights={loginHighlights}
      heroMetrics={loginMetrics}
      formBadge={isSelectingBusiness ? 'Escolher negócio' : 'Área do gestor'}
      formTitle={isSelectingBusiness ? 'Escolher negócio' : 'Entrar'}
      formDescription={
        isSelectingBusiness
          ? 'Esta conta tem acesso a vários espaços. Escolha onde quer entrar antes de abrir o painel.'
          : 'Volte a gerir marcações com uma interface mais leve, clara e segura.'
      }
      footer={
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm font-medium text-[#6b736d]">
          <button type="button" className="transition hover:text-[#16323b]">
            Política de Privacidade
          </button>
          <span className="text-[#b9ad9d]">•</span>
          <button type="button" className="transition hover:text-[#16323b]">
            Termos
          </button>
        </div>
      }
    >
      {isSelectingBusiness ? businessSelectionForm : loginForm}
    </AuthShell>
  );
}
